<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import LoginRequiredDialog from '@/components/auth/LoginRequiredDialog.vue'
import { useAuthGate } from '@/composables/useAuthGate'
import { useLoginForm } from '@/composables/useLoginForm'

/**
 * 全站共用的登入攔截對話框。
 *
 * 為什麼掛在 `App.vue` 而不是 `DefaultLayout`：遊戲頁刻意掛在版面外面
 * （見 `router/routes.ts`），而 session 可能在任何一頁失效。
 * 掛在版面裡的話，那些情況下對話框根本不存在。
 *
 * 為什麼是獨立元件而不是直接寫進 `App.vue`：那一頁刻意保持極簡
 * （T1 的 `App.vue` 有 415 行）。這裡的接線 —— 表單狀態、登入後要把
 * 什麼做完 —— 全部收在這個檔案裡。
 */
const { t } = useI18n()
const router = useRouter()
const gate = useAuthGate()

/**
 * 對話框裡的登入表單。
 *
 * `immediateCaptcha: false` —— 這個元件與整個站同壽命，用預設值的話
 * 每個訪客一進站就先跟後端要一張多數人不會用到的驗證碼。
 */
const login = useLoginForm({ immediateCaptcha: false })

const reasonText = computed(() => t(`authGate.reason.${gate.reason.value}`))

/**
 * 每次開啟都重置。
 *
 * 這個元件不會被卸載，所以上一次留下的帳號、紅字錯誤、以及那張早就
 * 用掉的驗證碼都還在。不重置的話使用者第二次開起來會看到別的東西。
 */
watch(gate.isOpen, (isOpen) => {
  if (isOpen) login.reset()
})

async function handleLogin(): Promise<void> {
  if (!(await login.submit())) return

  // 登入成功後把當初被擋下的那件事做完（進遊戲、導到存款頁……）
  gate.resume()
}

/** 沒帳號的出口。註冊是多步驟流程，塞不進彈窗，所以是導頁 */
function handleRegister(): void {
  gate.close()
  void router.push({ name: 'register' })
}
</script>

<template>
  <LoginRequiredDialog
    v-model:username="login.username.value"
    v-model:password="login.password.value"
    v-model:captcha-answer="login.captchaAnswer.value"
    :open="gate.isOpen.value"
    :reason-text="reasonText"
    :submitting="login.form.isSubmitting.value"
    :form-error="login.form.formError.value"
    :field-errors="login.form.fieldErrors.value"
    :captcha-svg="login.captcha.svg.value"
    :captcha-loading="login.captcha.isLoading.value"
    :captcha-failed="login.captcha.failed.value"
    @close="gate.close()"
    @submit="handleLogin"
    @refresh-captcha="login.captcha.refresh()"
    @register="handleRegister"
  />
</template>
